<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t(`${namespace}.incentives`) }}</span>
          </div>

          <v-layout class="table__header" align-center>
            <v-flex class="table__checkbox" style="max-width: 40px;" xs1 />
            <v-flex xs3>{{ $t('Views.IncentivesPage.requester') }}</v-flex>
            <v-flex xs3>{{ $t('Views.IncentivesPage.approver') }}</v-flex>
            <v-flex xs2>{{ $t('Views.IncentivesPage.createdDate') }}</v-flex>
            <v-flex xs3>{{ $t('Views.IncentivesPage.status') }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile class="table__body__tr">
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex class="table__checkbox table__td--prevent" style="max-width: 40px;" xs1>
                        <v-menu offset-y right>
                          <button class="table-body__actions" slot="activator">
                            <v-icon>
                              more_vertical
                            </v-icon>
                          </button>

                          <v-list class="dropdown-menu-actions__list">
                            <v-list-tile @click="openViewIncentivesDialog('success', item.id)">
                              <v-list-tile-title>{{$t(`${namespace}.linesWithSuccess`)}}</v-list-tile-title>
                            </v-list-tile>

                            <v-list-tile @click="openViewIncentivesDialog('error', item.id)">
                              <v-list-tile-title>{{$t(`${namespace}.linesWithErrors`)}}</v-list-tile-title>
                            </v-list-tile>

                            <v-divider
                              v-if="
                                hasRole('billing', 'ROLE_POST_INCENTIVE_APPROVED') &&
                                hasRole('billing', 'ROLE_GET_INCENTIVE_DETAIL') &&
                                item.status === 'PENDING_APPROVAL'
                              "
                            />

                            <v-list-tile
                               v-if="
                                hasRole('billing', 'ROLE_POST_INCENTIVE_APPROVED') &&
                                hasRole('billing', 'ROLE_GET_INCENTIVE_DETAIL') &&
                                item.status === 'PENDING_APPROVAL'
                              "
                              @click="openApproveIncentiveDialog(item)"
                            >
                              <v-list-tile-title>{{$t(`${namespace}.approveIncentive`)}}</v-list-tile-title>
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </v-flex>

                      <v-flex xs3>
                        <span v-if="item.createdBy && (item.createdBy.name || item.createdBy.email)">
                          <span class="text--ellipsis" :title="item.createdBy.name">
                            {{ item.createdBy.name }}
                          </span>
                          <span class="gray-6--text caption text--ellipsis">{{ item.createdBy.email }}</span>
                        </span>

                        <span v-else>-</span>
                      </v-flex>

                      <v-flex xs3>
                        <span v-if="item.approvedBy && (item.approvedBy.name || item.approvedBy.email)">
                          <span class="text--ellipsis" :title="item.approvedBy.name">
                            {{ item.approvedBy.name }}
                          </span>
                          <span class="gray-6--text caption text--ellipsis">{{ item.approvedBy.email }}</span>
                        </span>

                        <span v-else>-</span>
                      </v-flex>

                      <v-flex xs2>
                        <span v-if="item.startDate" class="text--ellipsis">
                          {{ moment(item.startDate).format('DD/MM/YY') }}
                        </span>

                        <span v-else>-</span>
                      </v-flex>

                      <v-flex xs3>
                        <StatusBullet
                          :status="getStatusBullet(item.status)"
                          :label="getStatusLabel(item.status)"
                        />
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="employee"
          :text="$t('Common.emptyFinancial')"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          style="margin-bottom: 120px"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <ApproveIncentiveDialog
      :visible="approveIncentiveDialog"
      :data="typeof selectedItem === 'object' ? selectedItem : {}"
      @close="closeApproveIncentiveDialog"
    />

    <ViewIncentivesDialog
      :visible="viewIncentivesDialog.visible"
      :data="typeof viewIncentivesDialog.data === 'object' ? viewIncentivesDialog.data : {}"
      :type="viewIncentivesDialog.type"
      @close="closeViewIncentivesDialog"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { hasRole, formatNumber, objectToArray } from 'utils/functions'
import EmptyState from 'components/EmptyState'
import StatusBullet from 'components/company/StatusBullet'
import ApproveIncentiveDialog from '../ApproveIncentiveDialog'
import ViewIncentivesDialog from '../ViewIncentivesDialog'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    drawerVisibility: {
      type: Boolean
    }
  },
  components: {
    EmptyState,
    StatusBullet,
    ApproveIncentiveDialog,
    ViewIncentivesDialog
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany
    }),

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.IncentivesPage.table',
      selectedItem: null,
      approveIncentiveDialog: false,
      viewIncentivesDialog: {
        visible: false,
        data: null,
        type: null
      }
    }
  },
  methods: {
    hasRole (group, role) {
      return hasRole(group, role)
    },

    getStatusBullet (status) {
      const statusList = {
        VALIDATION_STARTED: null,
        VALIDATION_FINISHED: null,
        VALIDATION_WITH_ERRORS: 'error',
        PENDING_APPROVAL: 'warning',
        APPROVED: 'warning-success',
        EXECUTION_STARTED: 'warning-success',
        EXECUTION_FINISHED: 'success',
        EXECUTION_FINISHED_WITH_ERRORS: 'success-warning'
      }

      return statusList[status] || status
    },

    getStatusLabel (status) {
      function getStatus (vm, value) {
        const match = objectToArray(vm.$t('Views.IncentivesPage.filters.statusList')).find(s => {
          return s.value === value
        })
        return match ? match.text : null
      }

      return getStatus(this, status) || '-'
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    openViewIncentivesDialog (type, id) {
      this.viewIncentivesDialog = {
        visible: true,
        data: { id },
        type
      }
    },

    closeViewIncentivesDialog () {
      this.viewIncentivesDialog = {
        visible: false,
        data: null,
        type: null
      }
    },

    openApproveIncentiveDialog (item) {
      this.approveIncentiveDialog = true
      this.selectedItem = item
    },

    closeApproveIncentiveDialog (reload) {
      this.approveIncentiveDialog = false
      this.selectedItem = null
      reload && this.handleReloadData()
    }
  }
}
</script>
